import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useApi, useWorkspace } from '~/contexts';

export default function useResourceMetrics({ resources, start, end, unit, metric, allocations, onFetched }) {
  const viewed = useRef([]);

  const { workspace } = useWorkspace();
  const api = useApi();

  const fetchData = useCallback(
    async ({ start, end, unit, metric, resourceIds, allocationIds }) => {
      if (resourceIds.length === 0) return;

      const { data: metrics } = await api.www.workspaces(workspace.id).allocations().memberSchedule.metrics.resources({
        start,
        end,
        unit,
        metric,
        resourceIds,
        allocationIds,
      });

      if (metrics.members.length === 0 && metrics.placeholders.length === 0) return;

      onFetched(metrics);
    },
    [api, workspace.id, onFetched],
  );

  const load = useCallback(
    ({ start, end, unit, metric, allocations }) => {
      const resourceIds = resources.map((r) => r.id);
      const allocationIds = (allocations ?? []).filter((a) => resourceIds.includes(a.resourceId)).map((a) => a.id);
      fetchData({ start, end, unit, metric, allocationIds, resourceIds });
      viewed.current = resourceIds;
    },
    [resources, fetchData],
  );

  const initialize = useCallback(() => {
    viewed.current = [];
  }, []);

  useEffect(() => {
    const resourceIds = resources.map((r) => r.id).filter((g) => !viewed.current.includes(g));
    if (resourceIds.length === 0) return;
    viewed.current = viewed.current.concat(resourceIds);
    const allocationIds = (allocations ?? []).filter((a) => resourceIds.includes(a.resourceId)).map((a) => a.id);
    fetchData({ start, end, unit, metric, allocationIds, resourceIds });
  }, [fetchData, start, end, unit, metric, allocations, resources]);

  return useMemo(() => ({ load, initialize }), [load, initialize]);
}
